import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAthleteProfile } from "../../../services/AthleteService";
import { useAxiosInstance } from "../../../services/Interceptor";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import {
  updateJerseyNumber,
  updateProfilePhoto,
  grantPositionDrillsAccess,
  removePositionDrillsAccess,
} from "../../../services/AdminService";
import {
  updateJerseyNumberAction,
  updatePositionDrillsAccessAction,
} from "../../../store/slices/eventSlice.jsx";
import { toast } from "react-toastify";
import { useResponsiveHeight } from "../../../hooks/useResponsiveHeight.js";
import AthleteInfo from "./AthleteInfo";
import AddJersey from "./AddJersey";
import AthleteTabs from "./AthleteTabs";

export default function CombinesDetails({
  backButtonClickEvent,
  updateProfilePicture,
}) {
  const dispatch = useDispatch();
  const r_event = useSelector((state) => state.events.attendees);
  const { userAthlete, eventId } = r_event;
  const apiInstance = useAxiosInstance();
  const [athleteData, setAthleteData] = useState();

  const [jerseyNumber, setJerseyNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingPhoto, setLoadingPhoto] = useState(false);
  const [loadingPositionDrills, setLoadingPositionDrills] = useState(false);

  const { adjustedHeight } = useResponsiveHeight({
    mobileOffset: 420,
    tabletOffset: 390,
    desktopOffset: 380,
  });

  useEffect(() => {
    const getProfile = async () => {
      try {
        const response = await getAthleteProfile(
          apiInstance,
          userAthlete.userId,
        );
        setAthleteData(response);
        setJerseyNumber(userAthlete.jerseyNumber || "");
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            "Failed to load athlete profile data",
          { position: "top-right" },
        );
      }
    };
    if (userAthlete && userAthlete.userId) {
      getProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAthlete?.userId]);

  const validateFile = (file) => {
    const permittedExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".mp4",
      ".mov",
    ];
    const permittedMimeTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "video/mp4",
      "video/quicktime",
    ];
    const maxFileSize = 100 * 1024 * 1024; // 100 MB

    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();
    const isExtensionValid = permittedExtensions.includes(fileExtension);
    const isMimeTypeValid = permittedMimeTypes.includes(file.type);
    const isSizeValid = file.size <= maxFileSize;

    if (!isExtensionValid) {
      toast.error(
        "Invalid file extension. Only JPG, PNG, GIF, MP4, and MOV are allowed.",
        { position: "top-right" },
      );
      return false;
    }

    if (!isMimeTypeValid) {
      toast.error("Invalid file type. Please upload an allowed file format.", {
        position: "top-right",
      });
      return false;
    }

    if (!isSizeValid) {
      toast.error("File size exceeds the 100 MB limit.", {
        position: "top-right",
      });
      return false;
    }

    return true;
  };

  const handleJerseyNumberUpdate = async () => {
    const trimmedJerseyNumber =
      jerseyNumber.trim() === "" ? null : jerseyNumber.trim();

    setLoading(true);
    try {
      await updateJerseyNumber(
        apiInstance,
        eventId,
        userAthlete.userId,
        trimmedJerseyNumber,
      );

      if (trimmedJerseyNumber === null) {
        toast.success("Jersey number successfully removed.");
      } else {
        toast.success("Jersey number successfully updated.");
      }

      updateProfilePicture();
      dispatch(
        updateJerseyNumberAction({
          userId: userAthlete.userId,
          jerseyNumber: trimmedJerseyNumber,
        }),
      );

      setAthleteData((prevData) => ({
        ...prevData,
        jerseyNumber: trimmedJerseyNumber,
      }));
    } catch (errorMessage) {
      toast.error(errorMessage, { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  const handleJerseyNumberChange = (e) => {
    setJerseyNumber(e.target.value);
  };

  const handleGrantPositionDrills = async () => {
    setLoadingPositionDrills(true);
    try {
      await grantPositionDrillsAccess(apiInstance, eventId, userAthlete.userId);
      toast.success("Position drills successfully given.");
      updateProfilePicture();
      dispatch(
        updatePositionDrillsAccessAction({
          userId: userAthlete.userId,
          hasPositionDrills: true,
          positionDrillGrantedByAdmin: true,
        }),
      );
    } catch (errorMessage) {
      toast.error(errorMessage, { position: "top-right" });
    } finally {
      setLoadingPositionDrills(false);
    }
  };

  const handleRemovePositionDrills = async () => {
    setLoadingPositionDrills(true);
    try {
      await removePositionDrillsAccess(
        apiInstance,
        eventId,
        userAthlete.userId,
      );
      toast.success("Position drills successfully removed.");
      updateProfilePicture();
      dispatch(
        updatePositionDrillsAccessAction({
          userId: userAthlete.userId,
          hasPositionDrills: false,
          positionDrillGrantedByAdmin: false,
        }),
      );
    } catch (errorMessage) {
      toast.error(errorMessage, { position: "top-right" });
    } finally {
      setLoadingPositionDrills(false);
    }
  };

  const handlePhotoUpload = async () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";
    // iOS Safari requires the input to be in the DOM to reliably fire change
    // events. Without this, uploads silently fail after ~3 uses.
    fileInput.style.display = "none";
    document.body.appendChild(fileInput);

    fileInput.onchange = async (event) => {
      document.body.removeChild(fileInput);
      const selectedFile = event.target.files[0];
      if (!selectedFile) {
        toast.error("Please select a photo to upload.");
        return;
      }
      if (!validateFile(selectedFile)) {
        return;
      }

      setLoadingPhoto(true);
      try {
        await updateProfilePhoto(apiInstance, userAthlete.userId, selectedFile);
        toast.success("Athlete photo updated successfully!");
        const updatedProfile = await getAthleteProfile(
          apiInstance,
          userAthlete.userId,
        );
        updateProfilePicture();
        setAthleteData(updatedProfile);
      } catch (error) {
        console.error("Error updating profile photo:", error);
        toast.error("Failed to update athlete photo.");
      } finally {
        setLoadingPhoto(false);
      }
    };

    // Clean up if the user dismisses the picker without selecting a file
    fileInput.addEventListener("cancel", () => {
      if (document.body.contains(fileInput)) {
        document.body.removeChild(fileInput);
      }
    });

    fileInput.click();
  };

  return eventId && userAthlete && athleteData ? (
    <div className="bg-white p-4 border-gray-300 rounded-md shadow-lg">
      <div className="athletes-list h-full event_details_mob md:absolute xl:relative lg:relative md:col-span-7 xl:col-span-7 xl:space-y-0 xl:bg-transparent xl:border-gray-300 p-0 md:space-y-0 md:top-0 xl:top-0 lg:top-0">
        <div className="text-blue-600 pt-6 px-6 md:visible xl:hidden lg:hidden sm:visible">
          <NavLink onClick={() => backButtonClickEvent(false)}>
            <FontAwesomeIcon icon={faChevronLeft} /> Back
          </NavLink>
        </div>
        <div className="p-4 xl:p-0 lg:p-0">
          <AthleteInfo
            athleteData={athleteData}
            userAthlete={userAthlete}
            loadingPhoto={loadingPhoto}
            onPhotoUpload={handlePhotoUpload}
          />
          <AddJersey
            jerseyNumber={jerseyNumber}
            userAthlete={userAthlete}
            loading={loading}
            onJerseyNumberChange={handleJerseyNumberChange}
            onJerseyNumberUpdate={handleJerseyNumberUpdate}
            loadingPositionDrills={loadingPositionDrills}
            onGrantPositionDrills={handleGrantPositionDrills}
            onRemovePositionDrills={handleRemovePositionDrills}
          />
          <AthleteTabs
            adjustedHeight={adjustedHeight}
            eventId={eventId}
            userAthlete={userAthlete}
          />
        </div>
      </div>
    </div>
  ) : null;
}
